/**
 * Transaction attribute denominations (issue #331).
 *
 * Each attribute may carry a per-language "denomination" — the visible label
 * plus a sign character that the frontend renders as a prefix in transaction
 * type selectors:
 *
 *   '+' → incoming (e.g. Buy, Cashback)
 *   '-' → outgoing (e.g. Sell, Withdrawal)
 *   '●' → neutral / operator (e.g. Transfer)
 *
 * Shape on the wire and in `bat.transaction_attributes.denominations`:
 *   { [languageCode]: { label: string, sign: AttributeSign } }
 *
 * Frontend display resolves the label via this fallback chain:
 *   1) denominations[userLocale].label  (if non-empty)
 *   2) denominations[defaultLocale].label  (defaultLocale from #330 catalog)
 *   3) i18n JSON translations.attributes[key].name  (legacy fallback)
 *   4) the raw `key`
 */

export const ATTRIBUTE_SIGN_VALUES = ['+', '-', '●'] as const

export type AttributeSign = (typeof ATTRIBUTE_SIGN_VALUES)[number]

export interface AttributeDenomination {
  label: string
  sign: AttributeSign
}

/**
 * Map keyed by language code (`es`, `en`, `de`, …).
 * Empty `{}` means "no denominations defined — fall back to legacy i18n JSON".
 */
export type AttributeDenominations = Record<string, AttributeDenomination>

/**
 * Pure resolver: given a denominations map, the user's preferred locale, and
 * the platform's default locale, return the best matching denomination or
 * `null` if none of the configured languages have a non-empty label.
 *
 * Exported so backend + frontend share ONE source of truth for the fallback
 * order. Both unit-test this directly.
 */
export function resolveDenomination(
  denominations: AttributeDenominations | null | undefined,
  userLocale: string,
  defaultLocale: string,
): AttributeDenomination | null {
  if (!denominations) return null

  const userEntry = denominations[userLocale]
  if (userEntry && userEntry.label.trim().length > 0) {
    return userEntry
  }

  if (defaultLocale !== userLocale) {
    const defaultEntry = denominations[defaultLocale]
    if (defaultEntry && defaultEntry.label.trim().length > 0) {
      return defaultEntry
    }
  }

  // Last resort: any other configured language with a non-empty label.
  // Order is deterministic via Object.keys to keep tests stable.
  for (const code of Object.keys(denominations)) {
    if (code === userLocale || code === defaultLocale) continue
    const entry = denominations[code]
    if (entry && entry.label.trim().length > 0) {
      return entry
    }
  }

  return null
}
